<script setup>
import { ref, computed, onMounted } from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import DefaultLayout from '@/layouts/default.vue';
import PositionsTable from '@/components/rbac/PositionsTable.vue';
import PositionFormDialog from '@/components/rbac/PositionFormDialog.vue';
import PermissionsMatrixDialog from '@/components/rbac/PermissionsMatrixDialog.vue';
import { useRbacStore } from '@/stores/rbac.js';
import { useToast } from 'primevue';

const rbacStore = useRbacStore();
const toast = useToast();
const positionsTableRef = ref(null);

const showAddDialog = ref(false);
const showEditDialog = ref(false);
const showPermissionsDialog = ref(false);
const selectedPosition = ref(null);
const selectedDepartment = ref(null);

// Загружаем отделы при монтировании
onMounted(async () => {
  if (rbacStore.departments.length === 0) {
    await rbacStore.loadDepartments();
  }
});

// Опции отделов для фильтра
const departmentOptions = computed(() => {
  return [
    { label: 'Все отделы', value: null },
    ...rbacStore.departments.map(dept => ({
      label: dept.name,
      value: dept.id
    }))
  ];
});

async function handleDepartmentFilter(deptId) {
  selectedDepartment.value = deptId;
  if (positionsTableRef.value) {
    positionsTableRef.value.filterByDepartment(deptId);
  }
}

async function handleCreate(positionData) {
  try {
    await rbacStore.createPosition(positionData);
    showAddDialog.value = false;
    if (positionsTableRef.value) {
      await positionsTableRef.value.filterByDepartment(selectedDepartment.value);
    }
    toast.add({
      severity: 'success',
      summary: 'Должность создана',
      detail: 'Должность успешно создана',
      life: 2500,
    });
  } catch (error) {
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось создать должность';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}

async function handleUpdate(positionData) {
  try {
    await rbacStore.updatePosition(selectedPosition.value.id, positionData);
    showEditDialog.value = false;
    selectedPosition.value = null;
    if (positionsTableRef.value) {
      await positionsTableRef.value.filterByDepartment(selectedDepartment.value);
    }
    toast.add({
      severity: 'success',
      summary: 'Должность обновлена',
      detail: 'Должность успешно обновлена',
      life: 2500,
    });
  } catch (error) {
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось обновить должность';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}

function handleEdit(position) {
  selectedPosition.value = position;
  showEditDialog.value = true;
}

async function handleDelete(position) {
  try {
    await rbacStore.deletePosition(position.id);
    if (positionsTableRef.value) {
      await positionsTableRef.value.filterByDepartment(selectedDepartment.value);
    }
    toast.add({
      severity: 'success',
      summary: 'Должность удалена',
      detail: 'Должность успешно удалена',
      life: 2500,
    });
  } catch (error) {
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
      ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
      : 'Не удалось удалить должность';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}

function handleConfigurePermissions(position) {
  selectedPosition.value = position;
  showPermissionsDialog.value = true;
}

function handlePermissionsSaved() {
  toast.add({
    severity: 'success',
    summary: 'Права сохранены',
    detail: 'Права доступа успешно обновлены',
    life: 2500,
  });
  showPermissionsDialog.value = false;
  selectedPosition.value = null;
}

function handleCloseEditDialog() {
  showEditDialog.value = false;
  selectedPosition.value = null;
}

function handleClosePermissionsDialog() {
  showPermissionsDialog.value = false;
  selectedPosition.value = null;
}
</script>

<template>
  <DefaultLayout>
    <Card class="w-full flex-1 flex flex-col min-h-0"
          :pt="{
            body: { class: 'flex-1 flex flex-col h-full' },
            content: { class: 'p-0 flex-1 flex flex-col min-h-0' }
          }"
    >
      <template #title>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <Select
              v-model="selectedDepartment"
              :options="departmentOptions"
              option-label="label"
              option-value="value"
              placeholder="Фильтр по отделу"
              class="w-64"
              @update:model-value="handleDepartmentFilter"
            />
          </div>
          <Button
            label="Добавить должность"
            icon="pi pi-plus"
            severity="info"
            rounded
            @click="showAddDialog = true"
          />
        </div>
      </template>

      <template #content>
        <PositionsTable
          ref="positionsTableRef"
          @edit="handleEdit"
          @delete="handleDelete"
          @configure-permissions="handleConfigurePermissions"
        />
      </template>
    </Card>

    <!-- Диалог создания должности -->
    <PositionFormDialog
      v-model:visible="showAddDialog"
      mode="create"
      @save="handleCreate"
    />

    <!-- Диалог редактирования должности -->
    <PositionFormDialog
      v-model:visible="showEditDialog"
      mode="edit"
      :position="selectedPosition"
      @save="handleUpdate"
      @close="handleCloseEditDialog"
    />

    <!-- Диалог настройки прав -->
    <PermissionsMatrixDialog
      v-model:visible="showPermissionsDialog"
      entity-type="position"
      :entity-id="selectedPosition?.id"
      :entity-name="selectedPosition?.name"
      @saved="handlePermissionsSaved"
      @close="handleClosePermissionsDialog"
    />
  </DefaultLayout>
</template>

<style scoped>
</style>
